import { memo, RefObject } from "react";
import { User, UserX } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { CommandPaletteResult, CommandPaletteResultGroup } from "../types";
import { CommandPaletteGroup } from "./CommandPaletteGroup";
import { PatientResultItemSkeleton } from "@/features/patient/components/PatientResultItemSkeleton";
import { EmptyState } from "@clarityos/ui";

const DEFAULT_SKELETON_COUNT = 3;
const SCROLL_CONTAINER_ID = "cmd-palette-scroll";

interface CommandPaletteResultsListProps {
  listRef: RefObject<HTMLDivElement | null>;
  results: CommandPaletteResultGroup[];
  flatResults: CommandPaletteResult[];
  onSelect: (result: CommandPaletteResult) => void;
  submittedQuery: string;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: VoidFunction;
  hasNextPage: boolean;
}

export const CommandPaletteResultsList = memo(
  ({
    listRef,
    results,
    flatResults,
    onSelect,
    submittedQuery,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  }: CommandPaletteResultsListProps) => {
    const renderPatientChildren = () => {
      if (isFetching) {
        return (
          <>
            {Array.from(
              { length: Math.max(DEFAULT_SKELETON_COUNT, flatResults.length) },
              (_, i) => (
                <PatientResultItemSkeleton key={i} />
              ),
            )}
          </>
        );
      }

      if (flatResults.length) return null;

      const emptyStateIconProps = {
        className: "mb-3 size-8 text-icon-disabled",
        strokeWidth: 1.5,
      };

      if (submittedQuery)
        return (
          <EmptyState
            icon={<UserX {...emptyStateIconProps} />}
            title="No patient found"
            subtitle={`No matches for "${submittedQuery}". Try a different name, DOB, or MRN.`}
          />
        );

      return (
        <EmptyState
          icon={<User {...emptyStateIconProps} />}
          subtitle="Type a patient's name, DOB, or MRN"
        />
      );
    };

    return (
      <div
        ref={listRef}
        id={SCROLL_CONTAINER_ID}
        className="max-h-[420px] overflow-y-auto"
      >
        <InfiniteScroll
          dataLength={flatResults.length}
          next={fetchNextPage}
          hasMore={hasNextPage}
          loader={isFetchingNextPage ? <PatientResultItemSkeleton /> : null}
          scrollableTarget={SCROLL_CONTAINER_ID}
        >
          {results.map((group) => (
            <CommandPaletteGroup
              key={group.group}
              group={group.group}
              items={isFetching && !isFetchingNextPage ? [] : group.items}
              flatResults={flatResults}
              onSelect={onSelect}
            >
              {group.group === "Patients" ? renderPatientChildren() : undefined}
            </CommandPaletteGroup>
          ))}
        </InfiniteScroll>
      </div>
    );
  },
);

CommandPaletteResultsList.displayName = "CommandPaletteResultsList";
